import { useQuery, useLazyQuery } from '@apollo/client';
import {
  Box,
  Button,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalMain,
  Paragraph,
  Pill,
  Stack,
  Table,
  TableColumns,
  Text,
  Tooltip,
} from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import { notNil } from '@sixfold/typed-primitives';
import React, { useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';

import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { InfiniteScroll } from '../../components/infinitescroll';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import {
  ApiEndpointUsageLogsReportDownloadUriQuery,
  ApiEndpointUsageLogsReportDownloadUriQueryVariables,
  CompanyIntegrationsListQuery,
  CompanyIntegrationsListQueryVariables,
  CompanyWebhookSubscriptionListItem,
  CompanyWebhookSubscriptionsQuery,
  CompanyWebhookSubscriptionsQueryVariables,
  getNodes,
  loadMoreFromConnection,
  notEmpty,
  PAGINATION_PAGE_SIZE,
  TelematicsIntegrationsListQuery,
  TelematicsIntegrationsListQueryVariables,
  UnifiedCompanySignupStatus,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { Routes } from '../../routes';
import {
  ApiEndpointUsageLogInList,
  ApiEndpointUsageLogsList,
} from '../../telematic_integration/components/api_endpoint_usage_log_list';
import { IntegrationList, useIntegrationListSort } from '../../telematic_integration/components/integration_list';
import { WEBHOOK_STATUS_COLORS } from '../../telematic_integration/components/webhook_subscription/webhook_subscription_table';
import { DeleteIntegrationMutation } from '../../telematic_integration/containers/delete_integration_container';
import { sortToGraphql } from '../../telematic_integration/containers/integrations_container';
import { TelematicIntegration } from '../../telematic_integration/entities';
import { telematicsIntegrationsListQuery } from '../../telematic_integration/graphql';
import {
  apiEndpointUsageLogsReportDownloadURIQuery,
  companyIntegrationsListQuery,
  companyWebhookSubscriptionsQuery,
} from '../graphql';
import { isCompanySignupStatusCompatibleForIntegration, getSignupStatusName } from '../utils';

import styles from './styles.module.css';

const WEBHOOK_STATUS_PILL_KIND: { [key: string]: 'success' | 'danger' | 'warning' | 'neutral' } = {
  green: 'success',
  red: 'danger',
  yellow: 'warning',
};

export interface ApiEndpointUsageListProps {
  api_endpoint_usage_logs_data: {
    api_endpoint_usage_logs: ApiEndpointUsageLogInList[];
    shipper_tours_count: number;
    carrier_tours_count: number;
  };
  company_name: string | null;
}

export interface WebhookSubscriptionsListContainerProps {
  webhook_subscriptions: CompanyWebhookSubscriptionListItem[];
}

export interface ListProps {
  integrations: TelematicIntegration[] | null;
}

class ApiEndpointUsageLoadingContainer extends Loader<
  CompanyIntegrationsListQuery,
  ApiEndpointUsageListProps,
  CompanyIntegrationsListQueryVariables
> {}
class LoadingContainer extends Loader<
  TelematicsIntegrationsListQuery,
  ListProps,
  TelematicsIntegrationsListQueryVariables
> {}
class WebhookSubscriptionsLoadingContainer extends Loader<
  CompanyWebhookSubscriptionsQuery,
  WebhookSubscriptionsListContainerProps,
  CompanyWebhookSubscriptionsQueryVariables
> {}

interface WebhookSubscriptionsListProps {
  webhookSubscriptions: CompanyWebhookSubscriptionListItem[];
  companyId: string;
  slugCompanyName?: string;
}

type WebhookSubscriptionRow = {
  id: string;
  url: string | null;
  topics: CompanyWebhookSubscriptionListItem['topics'];
  status: CompanyWebhookSubscriptionListItem['status'];
  lastStatusChange: CompanyWebhookSubscriptionListItem['lastStatusChange'];
  updatedAt: CompanyWebhookSubscriptionListItem['updatedAt'];
};

const WebhookSubscriptionsList: React.FunctionComponent<WebhookSubscriptionsListProps> = ({
  webhookSubscriptions,
  companyId,
  slugCompanyName,
}) => {
  const columns: TableColumns<WebhookSubscriptionRow, { companyId: string; slugCompanyName?: string }> = [
    {
      id: 'id',
      title: 'Subscription ID',
      width: '120px',
      cellContent: ({ value, context }) => (
        <NavLink
          to={Routes.CompanyWebhookSubscription.generatePath({
            company_id: context.companyId,
            webhook_subscription_id: value,
            slug_company_name: context.slugCompanyName,
          })}>
          {value}
        </NavLink>
      ),
    },
    {
      id: 'url',
      title: 'URL',
      width: '25%',
      cellContent: ({ value }) => value ?? '-',
    },
    {
      id: 'topics',
      title: 'Topics',
      width: '35%',
      cellContent: ({ value }) => (
        <Stack gap="xsmall" flexWrap="wrap" direction="row">
          {value.map((topic) => (
            <Pill key={topic} kind="info">
              {topic}
            </Pill>
          ))}
        </Stack>
      ),
    },
    {
      id: 'status',
      title: 'Status',
      width: '100px',
      cellContent: ({ value }) => (
        <Pill kind={WEBHOOK_STATUS_PILL_KIND[WEBHOOK_STATUS_COLORS[value]] ?? 'neutral'}>{value}</Pill>
      ),
    },
    {
      id: 'lastStatusChange',
      title: 'Last Status Change',
      width: 'auto',
      cellContent: ({ value }) => <FormattedDate date={value} />,
    },
    {
      id: 'updatedAt',
      title: 'Updated At',
      width: 'auto',
      cellContent: ({ value }) => <FormattedDate date={value} />,
    },
  ];

  const data: WebhookSubscriptionRow[] = webhookSubscriptions.map((s) => ({
    id: s.subscriptionId,
    url: s.url ?? null,
    topics: s.topics,
    status: s.status,
    lastStatusChange: s.lastStatusChange,
    updatedAt: s.updatedAt,
  }));

  if (data.length === 0) {
    return <p>No webhook subscriptions found.</p>;
  }

  return <Table data={data} columns={columns} context={{ companyId, slugCompanyName }} />;
};

export const CompanyIntegrationsContainer: React.FunctionComponent = () => {
  const { company_id, slug_company_name } = useParams() as { company_id: string; slug_company_name?: string };
  const hasIntegrationsEditPermission = useHasScopes([BackofficeScope.telematicsWriteIntegrations]);
  const isAdmin = useIsAdmin();
  const { sort } = useIntegrationListSort();
  const navigate = useNavigate();
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  const integrationsResult = useQuery<TelematicsIntegrationsListQuery, TelematicsIntegrationsListQueryVariables>(
    telematicsIntegrationsListQuery,
    {
      variables: {
        companyId: company_id,
        limit: PAGINATION_PAGE_SIZE,
        sort: sortToGraphql(sort),
      },
    },
  );

  const webhooksResult = useQuery<CompanyWebhookSubscriptionsQuery, CompanyWebhookSubscriptionsQueryVariables>(
    companyWebhookSubscriptionsQuery,
    { variables: { company_id } },
  );

  const apiEndpointUsageListResult = useQuery<CompanyIntegrationsListQuery, CompanyIntegrationsListQueryVariables>(
    companyIntegrationsListQuery,
    { variables: { company_id } },
  );

  const [fetchReportDownloadURI] = useLazyQuery<
    ApiEndpointUsageLogsReportDownloadUriQuery,
    ApiEndpointUsageLogsReportDownloadUriQueryVariables
  >(apiEndpointUsageLogsReportDownloadURIQuery, { fetchPolicy: 'network-only' });

  const handleExportApiUsage = async () => {
    try {
      const { data } = await fetchReportDownloadURI({ variables: { companyId: company_id } });
      const uri = data?.apiEndpointUsageLogsReportDownloadURI;
      if (uri) {
        window.location.href = uri;
      }
    } catch (error) {
      console.error('Failed to generate export URL', error);
    }
  };

  const unifiedSignupStatus: UnifiedCompanySignupStatus | null =
    apiEndpointUsageListResult.data?.company?.unifiedSignupStatus ?? null;
  const isStatusCompatible =
    apiEndpointUsageListResult.loading || isCompanySignupStatusCompatibleForIntegration(unifiedSignupStatus);
  const statusDisplayName = getSignupStatusName(unifiedSignupStatus);
  const addIntegrationPath = Routes.CompanyTelematicsIntegrationCreate.generatePath({ company_id, slug_company_name });

  const handleConfirmAddIntegration = () => {
    setIsWarningModalOpen(false);
    void navigate(addIntegrationPath);
  };

  return (
    <>
      <Box paddingBlock="medium">
        <Stack direction="row" alignItems="center" gap="small">
          <h2 style={{ margin: 0 }}>Integrations</h2>
          {(isAdmin || hasIntegrationsEditPermission) && (
            <>
              {isStatusCompatible ? (
                <Button kind="primary" size="small" onClick={() => void navigate(addIntegrationPath)}>
                  Add Integration
                </Button>
              ) : (
                <Tooltip content={`Company signup status is "${statusDisplayName}". Adding an integration may fail.`}>
                  <Button
                    iconStart="small-alert"
                    kind="primary"
                    size="small"
                    onClick={() => setIsWarningModalOpen(true)}>
                    Add Integration
                  </Button>
                </Tooltip>
              )}
              <Button
                kind="primary"
                size="small"
                onClick={() =>
                  void navigate(Routes.CompanyWebhookSubscriptionCreate.generatePath({ company_id, slug_company_name }))
                }>
                Add Webhook
              </Button>
            </>
          )}
        </Stack>
      </Box>{' '}
      {!isStatusCompatible && (
        <Modal trigger={null} open={isWarningModalOpen} onToggle={(open) => setIsWarningModalOpen(open)}>
          <ModalHeader title="Warning: Incompatible Company Status" />
          <ModalMain>
            <Paragraph>
              This company&apos;s signup status is &quot;{statusDisplayName}&quot;. Adding an integration for a company
              that is not in &quot;Visibility source pending&quot; or &quot;Onboarded&quot; status will likely fail.
            </Paragraph>
            <Paragraph>Do you still want to proceed?</Paragraph>
          </ModalMain>
          <ModalFooter>
            <Stack justifyContent="end" gap="small">
              <Button kind="subtle" onClick={() => setIsWarningModalOpen(false)}>
                Cancel
              </Button>
              <Button kind="primary" onClick={handleConfirmAddIntegration}>
                Proceed
              </Button>
            </Stack>
          </ModalFooter>
        </Modal>
      )}
      <DeleteIntegrationMutation companyId={company_id}>
        {({ deleteTelematicsIntegration }) => (
          <LoadingContainer
            result={integrationsResult}
            mapData={({ data }) => ({
              integrations: getNodes(data.telematicsIntegrations?.integrations),
              company_id,
            })}>
            {({ integrations }) => (
              <>
                <InfiniteScroll
                  loadMoreEntries={() =>
                    loadMoreFromConnection(telematicsIntegrationsListQuery, integrationsResult, [
                      'telematicsIntegrations',
                      'integrations',
                    ])
                  }>
                  <IntegrationList
                    integrations={integrations}
                    deleteTelematicsIntegration={deleteTelematicsIntegration}
                  />
                </InfiniteScroll>
              </>
            )}
          </LoadingContainer>
        )}
      </DeleteIntegrationMutation>
      <WebhookSubscriptionsLoadingContainer
        result={webhooksResult}
        mapData={({ data }) => ({
          webhook_subscriptions: (data.company?.companyWebhookSubscriptions ?? []).filter(notNil),
        })}>
        {({ webhook_subscriptions }) => (
          <Stack direction="column" gap="medium" style={{ paddingBlock: 'var(--size-spacing-medium)' }}>
            <h2 style={{ margin: 0 }}>Webhooks</h2>
            <WebhookSubscriptionsList
              webhookSubscriptions={webhook_subscriptions}
              companyId={company_id}
              slugCompanyName={slug_company_name}
            />
          </Stack>
        )}
      </WebhookSubscriptionsLoadingContainer>
      <ApiEndpointUsageLoadingContainer
        result={apiEndpointUsageListResult}
        mapData={({ data }) => ({
          api_endpoint_usage_logs_data: {
            api_endpoint_usage_logs: (
              data.company?.apiEndpointUsageLogs.map((log) => ({
                api_implemented_at: log.apiImplementedAt,
                counters: log.counters,
                endpoint_id: log.endpointId,
                endpoint_name: log.endpointName,
                first_call_at: log.firstCallAt,
                last_call_at: log.lastCallAt,
                integration_identifier: log.integrationIdentifier,
                webhook_subscription: log.webhookSubscription
                  ? {
                      ...log.webhookSubscription,
                      companyId: company_id,
                    }
                  : null,
                implementation_status: log.implementationStatus,
                api_health_status: log.apiHealthStatus,
              })) ?? []
            ).filter(notEmpty),
            shipper_tours_count: data.company?.shipperToursCount ?? 0,
            carrier_tours_count: data.company?.carrierToursCount ?? 0,
          },
          company_id,
          company_name: data.company?.company_name ?? null,
        })}>
        {({ api_endpoint_usage_logs_data, company_name }) => (
          <>
            <div className={styles.visibilityApiSection}>
              <Stack direction="row" alignItems="center" gap="small" className={styles.visibilityApiHeaderRow}>
                <h2 className={styles.visibilityApiHeading}>Visibility API</h2>
                <Button
                  iconStart="small-download"
                  size="small"
                  onClick={() => void handleExportApiUsage()}
                  title="Export to Excel">
                  <Text>Export</Text>
                </Button>
              </Stack>
              <ApiEndpointUsageLogsList
                data={[
                  {
                    company_id,
                    company_name: company_name ?? company_id,
                    api_endpoint_usage_logs_data,
                  },
                ]}
                isSingleCompany={true}
              />
            </div>
          </>
        )}
      </ApiEndpointUsageLoadingContainer>
    </>
  );
};
